import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Alert,
  
  Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Work as WorkIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

const API_BASE = API_BASE_URL;

interface JobPost {
  id: number;
  title: string;
  company: string;
  location: string;
  profession: string;
  description: string;
  requirements?: string;
  salary?: string;
  contactEmail: string;
  contactPhone?: string;
  applicationDeadline?: string;
  status: string;
  createdAt: string;
  User?: {
    name: string;
    email: string;
  };
}

const TransferHubJobs: React.FC = () => {
  const { token } = useAuth();
  const [jobs, setJobs] = useState<JobPost[]>([]);
  const [myJobs, setMyJobs] = useState<JobPost[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPost | null>(null);
  const [message, setMessage] = useState<string>('');
  const [filter, setFilter] = useState({ profession: '', location: '' });

  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    profession: '',
    description: '',
    requirements: '',
    salary: '',
    contactEmail: '',
    contactPhone: '',
    applicationDeadline: ''
  });

  const loadJobs = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filter.profession) queryParams.append('profession', filter.profession.toLowerCase().trim());
      if (filter.location) queryParams.append('location', filter.location.toLowerCase().trim());

      const res = await fetch(`${API_BASE}/transferhub/jobs?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  }, [token, filter.profession, filter.location]);

  const loadMyJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/transferhub/jobs/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyJobs(data);
      }
    } catch (error) {
      console.error('Failed to load my jobs:', error);
    }
  }, [token]);

  useEffect(() => {
    loadJobs();
    loadMyJobs();
  }, [loadJobs, loadMyJobs]);

  

  const handleSubmit = async () => {
    try {
      const url = editingJob 
        ? `${API_BASE}/transferhub/jobs/${editingJob.id}`
        : `${API_BASE}/transferhub/jobs`;
      
      const method = editingJob ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setMessage(editingJob ? 'Job updated successfully' : 'Job posted successfully');
        setOpenDialog(false);
        setEditingJob(null);
        resetForm();
        loadJobs();
        loadMyJobs();
      } else {
        setMessage('Failed to save job');
      }
    } catch (error) {
      setMessage('Failed to save job');
    }
  };

  const handleDelete = async (jobId: number) => {
    if (!window.confirm('Are you sure you want to delete this job post?')) return;

    try {
      const res = await fetch(`${API_BASE}/transferhub/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setMessage('Job deleted successfully');
        loadJobs();
        loadMyJobs();
      } else {
        setMessage('Failed to delete job');
      }
    } catch (error) {
      setMessage('Failed to delete job');
    }
  };

  const handleEdit = (job: JobPost) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      company: job.company,
      location: job.location,
      profession: job.profession,
      description: job.description,
      requirements: job.requirements || '',
      salary: job.salary || '',
      contactEmail: job.contactEmail,
      contactPhone: job.contactPhone || '',
      applicationDeadline: job.applicationDeadline ? job.applicationDeadline.split('T')[0] : ''
    });
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      company: '',
      location: '',
      profession: '',
      description: '',
      requirements: '',
      salary: '',
      contactEmail: '',
      contactPhone: '',
      applicationDeadline: ''
    });
  };

  const openNewJobDialog = () => {
    setEditingJob(null);
    resetForm();
    setOpenDialog(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <WorkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Job Posts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openNewJobDialog}
        >
          Post New Job
        </Button>
      </Box>

      {message && <Alert sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Jobs
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Profession"
                value={filter.profession}
                onChange={(e) => setFilter({ ...filter, profession: e.target.value })}
                placeholder="e.g., Software Developer"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadJobs();
                  }
                }}
                InputProps={{
                  endAdornment: filter.profession && (
                    <IconButton size="small" onClick={() => setFilter({ ...filter, profession: '' })}>
                      <ClearIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Location"
                value={filter.location}
                onChange={(e) => setFilter({ ...filter, location: e.target.value })}
                placeholder="e.g., Cape Town"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    loadJobs();
                  }
                }}
                InputProps={{
                  endAdornment: filter.location && (
                    <IconButton size="small" onClick={() => setFilter({ ...filter, location: '' })}>
                      <ClearIcon />
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button 
                variant="contained" 
                onClick={loadJobs}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: '56px' }}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button 
                variant="outlined" 
                onClick={() => { 
                  setFilter({ profession: '', location: '' }); 
                  setTimeout(() => loadJobs(), 0); 
                }}
                startIcon={<ClearIcon />}
                size="small"
              >
                Clear All Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* My Job Posts */}
      {myJobs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>My Job Posts</Typography>
          <Grid container spacing={2}>
            {myJobs.map((job) => (
              <Grid item xs={12} md={6} key={job.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>{job.title}</Typography>
                        <Typography color="textSecondary" gutterBottom>{job.company}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip label={job.location} size="small" />
                          <Chip label={job.profession} size="small" variant="outlined" />
                          <Chip 
                            label={job.status} 
                            size="small" 
                            color={job.status === 'active' ? 'success' : 'default'}
                          />
                        </Box>
                                          <Typography variant="body2" sx={{ mb: 1 }}>
                    {job.description.substring(0, 150)}...
                  </Typography>
                  {job.requirements && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Requirements:</strong> {job.requirements.substring(0, 100)}...
                    </Typography>
                  )}
                  {job.salary && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Salary:</strong> {job.salary}
                    </Typography>
                  )}
                  {job.contactPhone && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> {job.contactPhone}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary">
                    <strong>Contact:</strong> {job.contactEmail}
                  </Typography>
                      </Box>
                      <Box>
                        <IconButton onClick={() => handleEdit(job)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(job.id)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* All Job Posts */}
      <Typography variant="h5" gutterBottom>Available Jobs</Typography>
      {jobs.length === 0 ? (
        <Card>
          <CardContent>
            <Typography textAlign="center" color="textSecondary">
              No job posts found. Try adjusting your search filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {jobs.map((job) => (
            <Grid item xs={12} md={6} key={job.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{job.title}</Typography>
                  <Typography color="textSecondary" gutterBottom>{job.company}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={job.location} size="small" />
                    <Chip label={job.profession} size="small" variant="outlined" />
                    <Chip 
                      label={job.status} 
                      size="small" 
                      color={job.status === 'active' ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {job.description.substring(0, 150)}...
                  </Typography>
                  {job.requirements && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Requirements:</strong> {job.requirements.substring(0, 100)}...
                    </Typography>
                  )}
                  {job.salary && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Salary:</strong> {job.salary}
                    </Typography>
                  )}
                  {job.contactPhone && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> {job.contactPhone}
                    </Typography>
                  )}
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    <strong>Contact:</strong> {job.contactEmail}
                  </Typography>
                  {job.applicationDeadline && (
                    <Typography variant="body2" color="textSecondary">
                      Deadline: {new Date(job.applicationDeadline).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Job Post Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingJob ? 'Edit Job Post' : 'Post New Job'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Profession"
                value={formData.profession}
                onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requirements (optional)"
                value={formData.requirements}
                onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Salary (optional)"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                placeholder="e.g., R25,000 - R35,000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Application Deadline (optional)"
                type="date"
                value={formData.applicationDeadline}
                onChange={(e) => setFormData({ ...formData, applicationDeadline: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone (optional)"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingJob ? 'Update Job' : 'Post Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransferHubJobs;







